/** SUNTRONIX ONE — Tax Registrations Editor with inline India validation.
 *
 *  Route: /customer/:mcId/tax
 *
 *  Adds / edits tax registrations against a Customer Master.  For every
 *  row we run:
 *    1. Client-side format regex (GSTIN / PAN / CIN / EIN / VAT).
 *    2. On blur — server-side POST /master-clients/validators/india-tax
 *       which performs the Mod-36 checksum on GSTIN and re-validates PAN
 *       / CIN.  This means the operator sees checksum errors BEFORE
 *       clicking Save.
 *  Save routes through the standard PATCH — which will hit the
 *  change-request pipe when the tenant has the workflow enabled.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import Customer360Header from "@/components/app/Customer360Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, AlertCircle, Plus, Trash2, Save, Loader2, ShieldCheck } from "lucide-react";

const TAX_TYPES_BY_COUNTRY = {
  IN: [{ v: "GSTIN", l: "GSTIN" }, { v: "PAN", l: "PAN" },
        { v: "CIN", l: "CIN" }, { v: "TAN", l: "TAN" }],
  US: [{ v: "EIN", l: "EIN" }, { v: "SSN", l: "SSN" }],
  GB: [{ v: "VAT", l: "VAT (UK)" }, { v: "CRN", l: "CRN" }],
  EU: [{ v: "VAT", l: "VAT" }],
  DEFAULT: [{ v: "VAT", l: "VAT" }, { v: "GST", l: "GST" },
              { v: "TIN", l: "TIN" }, { v: "OTHER", l: "Other" }],
};

// Client-side format checks — the SERVER also validates, but showing an
// instant hint feels responsive.  Regex must match backend definitions.
const RE = {
  GSTIN: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
  PAN:   /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
  CIN:   /^[LUu][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
};

const HELP = {
  GSTIN: "15 chars · 22AAAAA0000A1Z5 — state code + PAN + entity + Z + check",
  PAN:   "10 chars · AAAAA9999A",
  CIN:   "21 chars · L12345MH2020PLC123456",
  EIN:   "US federal ID · 9 digits (NN-NNNNNNN)",
  VAT:   "Country-specific VAT identifier",
};

function emptyRow() {
  return {
    tax_type: "GSTIN", identifier: "", country: "IN",
    jurisdiction: "", legal_name: "", verified: false,
    _clientError: null, _serverError: null, _validating: false,
    _serverOk: false,
  };
}

export default function TaxRegistrationsEditor() {
  const { mcId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/master-clients/${mcId}`);
      const list = (data.tax_registrations || []).map((r) => ({
        ...emptyRow(), ...r,
      }));
      setRows(list.length ? list : [emptyRow()]);
    } catch (e) {
      toast.error("Could not load registrations");
    } finally { setLoading(false); }
  }, [mcId]);

  useEffect(() => { load(); }, [load]);

  const runClientCheck = useCallback((row) => {
    const t = (row.tax_type || "").toUpperCase();
    const id = (row.identifier || "").toUpperCase().trim();
    if (!id) return "Identifier is required";
    if (RE[t] && !RE[t].test(id)) {
      return `${t} format invalid — expected ${HELP[t]}`;
    }
    return null;
  }, []);

  const validateOnBlur = useCallback(async (idx) => {
    setRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[idx] };
      row._clientError = runClientCheck(row);
      copy[idx] = row;
      return copy;
    });
    const row = rows[idx];
    if (!row || !row.identifier) return;
    // Only ping the server when country is IN — that's what the endpoint
    // actually validates.  Other countries stay on client-side format only.
    if ((row.country || "").toUpperCase() !== "IN") return;
    setRows((prev) => {
      const c = [...prev]; c[idx] = { ...c[idx], _validating: true }; return c;
    });
    try {
      const { data } = await api.post(
        "/master-clients/validators/india-tax",
        { tax_type: row.tax_type, identifier: (row.identifier || "").toUpperCase(),
          country: row.country });
      setRows((prev) => {
        const c = [...prev];
        c[idx] = {
          ...c[idx], _validating: false,
          _serverError: data.ok ? null : data.error,
          _serverOk:    data.ok === true,
        };
        return c;
      });
    } catch {
      setRows((prev) => {
        const c = [...prev];
        c[idx] = { ...c[idx], _validating: false, _serverError: null };
        return c;
      });
    }
  }, [rows, runClientCheck]);

  const setField = (idx, key, value) => {
    setRows((prev) => {
      const c = [...prev];
      c[idx] = { ...c[idx], [key]: value,
                    _clientError: null, _serverError: null, _serverOk: false };
      return c;
    });
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) =>
    setRows((prev) => prev.filter((_, i) => i !== idx));

  const anyErrors = useMemo(
    () => rows.some((r) => r._clientError || r._serverError),
    [rows]);

  const canSave = rows.length > 0 && !anyErrors && !saving;

  const save = async () => {
    // Final client-side check on every row before firing PATCH
    let localErrors = false;
    const withChecks = rows.map((r) => {
      const err = runClientCheck(r);
      if (err) localErrors = true;
      return { ...r, _clientError: err };
    });
    setRows(withChecks);
    if (localErrors) {
      toast.error("Fix formatting errors before saving");
      return;
    }
    setSaving(true);
    try {
      const clean = rows.map((r) => ({
        tax_type: r.tax_type, identifier: (r.identifier || "").toUpperCase(),
        country: (r.country || "").toUpperCase(),
        jurisdiction: r.jurisdiction || null,
        legal_name: r.legal_name || null,
        trade_name: r.trade_name || null,
        verified: !!r.verified,
        valid_from: r.valid_from || null,
        valid_until: r.valid_until || null,
      }));
      const { data } = await api.patch(
        `/master-clients/${mcId}/tax-registrations`,
        clean, { params: reason ? { reason } : {} });
      if (data.routed_to_change_request) {
        toast.success("Change routed to approval workflow (CR " +
                        (data.change_request?.code || "…") + ")");
      } else {
        toast.success(`Saved · ${data.count} registration${data.count === 1 ? "" : "s"}`);
      }
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.issues) {
        toast.error("Validation failed: " + detail.issues.join(" · "));
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="tax-registrations-editor">
      <PageHeader
        eyebrow="Customer 360"
        title="Tax registrations"
        context="Manage tax identifiers — validated live before save."
        actions={
          <Button onClick={save} disabled={!canSave} data-testid="tax-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" strokeWidth={1.8} />}
            Save
          </Button>
        }
        testId="tax-editor-header"
      />

      <Customer360Header masterClientId={mcId} compact />

      <Section title="Registrations" hint="Format and checksum validated on blur.  India registrations run through server-side Mod-36 checksum." testId="tax-section">
        {loading ? (
          <div className="text-slate-400 text-[13px]">Loading…</div>
        ) : (
          <div className="space-y-3">
            {rows.map((r, idx) => {
              const err = r._clientError || r._serverError;
              const status = r._validating ? "checking"
                : err ? "error"
                : r._serverOk ? "ok"
                : r.identifier ? "idle" : "empty";
              return (
                <div key={idx} className="st-panel p-3"
                  data-testid={`tax-row-${idx}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                    <div className="md:col-span-2">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Country</Label>
                      <Select value={r.country}
                        onValueChange={(v) => setField(idx, "country", v)}>
                        <SelectTrigger data-testid={`tax-country-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["IN","US","GB","AE","SG","AU","DE","FR","CA"].map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Type</Label>
                      <Select value={r.tax_type}
                        onValueChange={(v) => setField(idx, "tax_type", v)}>
                        <SelectTrigger data-testid={`tax-type-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(TAX_TYPES_BY_COUNTRY[r.country] || TAX_TYPES_BY_COUNTRY.DEFAULT).map((t) => (
                            <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-4">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Identifier</Label>
                      <div className="relative">
                        <Input value={r.identifier}
                          onChange={(e) => setField(idx, "identifier", e.target.value)}
                          onBlur={() => validateOnBlur(idx)}
                          className={"font-mono uppercase " +
                            (status === "error" ? "border-red-400" :
                             status === "ok"    ? "border-emerald-400" : "")}
                          placeholder={HELP[r.tax_type] || "Identifier"}
                          data-testid={`tax-identifier-${idx}`} />
                        <span className="absolute right-2 top-2.5">
                          {status === "checking" && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                          {status === "ok" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                          {status === "error" && <AlertCircle className="h-3.5 w-3.5 text-red-500" />}
                        </span>
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Jurisdiction</Label>
                      <Input value={r.jurisdiction || ""}
                        onChange={(e) => setField(idx, "jurisdiction", e.target.value)}
                        placeholder="e.g. Maharashtra"
                        data-testid={`tax-jurisdiction-${idx}`} />
                    </div>
                    <div className="md:col-span-1 text-right">
                      <button onClick={() => removeRow(idx)}
                        className="text-red-500 hover:text-red-700 p-1"
                        data-testid={`tax-delete-${idx}`}
                        aria-label="Remove">
                        <Trash2 className="h-4 w-4" strokeWidth={1.6} />
                      </button>
                    </div>
                  </div>
                  {err && (
                    <div className="mt-2 flex items-start gap-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1"
                      data-testid={`tax-error-${idx}`}>
                      <AlertCircle className="h-3 w-3 mt-0.5" strokeWidth={2} />
                      <span>{err}</span>
                    </div>
                  )}
                  {status === "ok" && (
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-700"
                      data-testid={`tax-ok-${idx}`}>
                      <ShieldCheck className="h-3 w-3" strokeWidth={2} />
                      <span>Format + checksum verified by server</span>
                    </div>
                  )}
                </div>
              );
            })}

            <button onClick={addRow}
              className="text-[12px] text-[color:var(--st-accent)] hover:underline inline-flex items-center gap-1"
              data-testid="tax-add-row">
              <Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add registration
            </button>
          </div>
        )}
      </Section>

      <Section title="Reason (optional)" hint="Included in the change-request audit trail when the CR workflow is active." testId="tax-reason-section">
        <Input value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. New state registration issued"
          data-testid="tax-reason" />
      </Section>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(-1)}
          data-testid="tax-cancel-btn">Cancel</Button>
        <Button onClick={save} disabled={!canSave}
          data-testid="tax-save-btn-bottom">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" strokeWidth={1.8} />}
          Save
        </Button>
      </div>
    </div>
  );
}
